import { useQuery } from "@tanstack/react-query";
import { ClientsPage } from "components/feature";
import { PageHeader } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { DetailPageLayout } from "components/shared/detail-page-layout";
import { Subnav } from "components/shared/subnav";
import { SubnavItem } from "components/shared/subnav/subnav-types";
import { BlankSlate } from "components/ui/blank-slate";
import { InlineBanner } from "components/ui/inline-banner";
import { Spinner } from "components/ui/spinner";
import * as React from "react";
import { useCallback, useState } from "react";
import { useRouteMatch } from "react-router";
import { navigate } from "store/actions";
import { useRegionId } from "store/selectors/hooks";
import { useCanTransferClient } from "store/selectors/settings";
import { TransferCardList } from "./transfer-card";
import {
    emptyTransferFilter,
    filterTransferRows,
    TransferFilter,
} from "./transfer-filter";
import { TransferFilterBar } from "./transfer-filter-bar";
import { TransferDirection, TransferRow } from "./transfer-types";

interface TransferTabProps {
    direction: TransferDirection;
    isLoading: boolean;
    isError: boolean;
    errorMessage: string;
    emptyMessage: string;
    rows: TransferRow[];
    filter: TransferFilter;
    onFilterChange: (filter: TransferFilter) => void;
    onTransferChanged: () => void;
}

const TransferTabBody: React.FC<TransferTabProps> = props => {
    const {
        direction,
        isLoading,
        isError,
        errorMessage,
        emptyMessage,
        rows,
        filter,
        onTransferChanged,
    } = props;

    if (isLoading) {
        return (
            <div className="flex justify-center p-6">
                <Spinner />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="max-w-2xl">
                <InlineBanner type="error">{errorMessage}</InlineBanner>
            </div>
        );
    }

    if (rows.length === 0) {
        return (
            <div className="max-w-2xl">
                <BlankSlate>{emptyMessage}</BlankSlate>
            </div>
        );
    }

    const filteredRows = filterTransferRows(rows, filter);

    if (filteredRows.length === 0) {
        return (
            <div className="max-w-2xl">
                <BlankSlate>No transfers match your search.</BlankSlate>
            </div>
        );
    }

    return (
        <TransferCardList
            direction={direction}
            rows={filteredRows}
            onTransferChanged={onTransferChanged}
        />
    );
};

const TransferTab: React.FC<TransferTabProps> = props => (
    <div className="space-y-4 pt-2">
        <TransferFilterBar filter={props.filter} onChange={props.onFilterChange} />
        <TransferTabBody {...props} />
    </div>
);

const noSentTransfers = "You have not sent a client to another agency yet.";

const useSentEmptyMessage = (): string => {
    const canStartTransfer = useCanTransferClient();
    return canStartTransfer
        ? `${noSentTransfers} Start one from the actions menu on a client's page.`
        : noSentTransfers;
};

const pendingCount = (rows: TransferRow[]): number =>
    rows.filter(row => row.status === "Pending").length;

export const ClientTransfers: React.FunctionComponent = () => {
    const regionId = useRegionId();
    const trpcContext = trpc.useTRPC();
    const routeMatch = useRouteMatch<{ subpage?: string }>("/transfers/:subpage");
    const [filter, setFilter] = useState<TransferFilter>(emptyTransferFilter);
    const sentEmptyMessage = useSentEmptyMessage();

    const incoming = useQuery(
        trpcContext.clientTransfer.incoming.queryOptions({ regionId }),
    );
    const outgoing = useQuery(
        trpcContext.clientTransfer.outgoing.queryOptions({ regionId }),
    );

    const refetchIncoming = incoming.refetch;
    const refetchOutgoing = outgoing.refetch;

    const refreshTransfers = useCallback(async () => {
        await Promise.all([refetchIncoming(), refetchOutgoing()]);
    }, [refetchIncoming, refetchOutgoing]);

    const handleNavigate = useCallback((subpage: string) => {
        navigate(`/transfers/${subpage}`);
    }, []);
    const handleCloseSection = useCallback(() => {
        navigate("/transfers");
    }, []);

    const pendingReceived = pendingCount(incoming.data ?? []);

    const subpages: SubnavItem[] = [
        {
            href: "/transfers/incoming",
            subpage: "incoming",
            linkText: "Received",
            badge: pendingReceived > 0 ? { count: pendingReceived, type: "info" } : null,
            render: () => (
                <TransferTab
                    direction="incoming"
                    isLoading={incoming.isLoading}
                    isError={incoming.isError}
                    errorMessage="Unable to load received transfers."
                    emptyMessage="No one has sent you a client yet."
                    rows={incoming.data ?? []}
                    filter={filter}
                    onFilterChange={setFilter}
                    onTransferChanged={refreshTransfers}
                />
            ),
        },
        {
            href: "/transfers/outgoing",
            subpage: "outgoing",
            linkText: "Sent",
            render: () => (
                <TransferTab
                    direction="outgoing"
                    isLoading={outgoing.isLoading}
                    isError={outgoing.isError}
                    errorMessage="Unable to load sent transfers."
                    emptyMessage={sentEmptyMessage}
                    rows={outgoing.data ?? []}
                    filter={filter}
                    onFilterChange={setFilter}
                    onTransferChanged={refreshTransfers}
                />
            ),
        },
    ];

    return (
        <ClientsPage>
            <div>
                <PageHeader title="Transfers" />
                <DetailPageLayout>
                    <div className="pt-2">
                        <Subnav
                            navigate={handleNavigate}
                            selectedSubpage={routeMatch?.params.subpage || null}
                            subpages={subpages}
                            closeSection={handleCloseSection}
                            defaultSubpage="incoming"
                        />
                    </div>
                </DetailPageLayout>
            </div>
        </ClientsPage>
    );
};
